import type { CompanyMoneyFormatWithSymbol } from './moneyFormatter';
import { assertValue, isDefined } from './types';

const defaultSupportPage = 'https://sembo.freshdesk.com/en/support/home';

class DomainMapping {
  private currentDomainSpec: Market | undefined;

  public getDomainSpec(domain: Domains): Market {
    switch (domain) {
      case Domains.SWEDEN:
        return {
          Number: 1,
          Domain: 'sembo.se',
          IsSubdomain: false,
          Locale: 'sv',
          FullLocale: 'sv-SE',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'SEK',
            currencySymbol: 'kr',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Bygg din resa - Hotell, flyg, färjor, aktiviteter, enstaka eller flera stopp!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/sv-SE/support/home',
        };
      case Domains.NORWAY:
        return {
          Number: 4,
          Domain: 'sembo.no',
          IsSubdomain: false,
          Locale: 'no',
          FullLocale: 'nb-NO',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'NOK',
            currencySymbol: 'kr',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Bygg turen din - Hoteller, fly, ferger, aktiviteter, enkelt eller flere stopp!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/nb-NO/support/home',
        };
      case Domains.FINLAND:
        return {
          Number: 6,
          Domain: 'sembo.fi',
          IsSubdomain: false,
          Locale: 'fi',
          FullLocale: 'fi-FI',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Rakenna matkasi - Hotellit, lennot, lautat, aktiviteetit, yksi tai useampi pysähdys!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/fi/support/home',
        };
      case Domains.GERMANY:
        return {
          Number: 7,
          Domain: 'sembo.de',
          IsSubdomain: false,
          Locale: 'de',
          FullLocale: 'de-DE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Stelle deine Reise zusammen - Hotels, Flüge, Fähren, Aktivitäten, einzelne oder mehrere Stopps!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/de/support/home',
        };
      case Domains.POLAND:
        return {
          Number: 8,
          Domain: 'sembo.pl',
          IsSubdomain: false,
          Locale: 'pl',
          FullLocale: 'pl-PL',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'PLN',
            currencySymbol: 'zł',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Zbuduj swoją podróż - Hotele, loty, promy, aktywności, pojedyncze lub wielokrotne przystanki!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.NETHERLAND:
        return {
          Number: 9,
          Domain: 'sembo.nl',
          IsSubdomain: false,
          Locale: 'nl',
          FullLocale: 'nl-NL',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Stel je reis samen - Hotels, vluchten, veerboten, activiteiten, enkele of meerdere stops!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/nl/support/home',
        };
      case Domains.UNITEDKINGDOM:
        return {
          Number: 10,
          Domain: 'sembo.co.uk',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-GB',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'GBP',
            currencySymbol: '£',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Build your trip - Hotels, flights, ferries, activities, single or multiple stops!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.IRELAND:
        return {
          Number: 11,
          Domain: 'sembo.ie',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-IE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.AUSTRIA:
        return {
          Number: 12,
          Domain: 'sembo.at',
          IsSubdomain: false,
          Locale: 'de',
          FullLocale: 'de-AT',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/de/support/home',
        };
      case Domains.HUNGARY:
        return {
          Number: 13,
          Domain: 'sembo.hu',
          IsSubdomain: false,
          Locale: 'hu',
          FullLocale: 'hu-HU',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'HUF',
            currencySymbol: 'Ft',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.AUSTRALIA:
        return {
          Number: 14,
          Domain: 'sembo.com.au',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-AU',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'AUD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.NEWZEELAND:
        return {
          Number: 15,
          Domain: 'sembo.nz',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-NZ',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'NZD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.USA:
        return {
          Number: 16,
          Domain: 'sembo.com',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-US',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'USD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CANADA:
        return {
          Number: 17,
          Domain: 'sembo.ca',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-CA',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'CAD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SOUTHAFRICA:
        return {
          Number: 18,
          Domain: 'sembo.co.za',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-ZA',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'ZAR',
            currencySymbol: 'R',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SINGAPORE:
        return {
          Number: 19,
          Domain: 'sembo.sg',
          IsSubdomain: false,
          Locale: 'en',
          FullLocale: 'en-SG',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'SGD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SPAIN:
        return {
          Number: 20,
          Domain: 'sembo.es',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-ES',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Construye tu viaje - Hoteles, vuelos, ferries, actividades, paradas individuales o múltiples!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/es/support/home',
        };
      case Domains.PORTUGAL:
        return {
          Number: 21,
          Domain: 'sembo.pt',
          IsSubdomain: false,
          Locale: 'pt',
          FullLocale: 'pt-PT',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.UNITEDARABEMIRATES:
        return {
          Number: 23,
          Domain: 'ae.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-AE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'AED',
            currencySymbol: 'د.إ',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BULGARIA:
        return {
          Number: 24,
          Domain: 'sembo.bg',
          IsSubdomain: false,
          Locale: 'bg',
          FullLocale: 'bg-BG',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'BGN',
            currencySymbol: 'лв',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CZECHREPUBLIC:
        return {
          Number: 25,
          Domain: 'cz.sembo.com',
          IsSubdomain: true,
          Locale: 'cs',
          FullLocale: 'cs-CZ',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'CZK',
            currencySymbol: 'Kč',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.INDIA:
        return {
          Number: 26,
          Domain: 'sembo.in',
          IsSubdomain: false,
          Locale: 'hi',
          FullLocale: 'hi-IN',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'INR',
            currencySymbol: '₹',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.PHILIPPINES:
        return {
          Number: 27,
          Domain: 'ph.sembo.com',
          IsSubdomain: true,
          Locale: 'en',
          FullLocale: 'en-PH',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'PHP',
            currencySymbol: '₱',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.NIGERIA:
        return {
          Number: 28,
          Domain: 'ng.sembo.com',
          IsSubdomain: true,
          Locale: 'en',
          FullLocale: 'en-NG',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'NGN',
            currencySymbol: '₦',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.MEXICO:
        return {
          Number: 29,
          Domain: 'sembo.com.mx',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-MX',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'MXN',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ARGENTINA:
        return {
          Number: 30,
          Domain: 'sembo.com.ar',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-AR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'ARS',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.COLOMBIA:
        return {
          Number: 31,
          Domain: 'sembo.com.co',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-CO',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'COP',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CHILE:
        return {
          Number: 32,
          Domain: 'sembo.cl',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-CL',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'CLP',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.PERU:
        return {
          Number: 33,
          Domain: 'sembo.pe',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-PE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'PEN',
            currencySymbol: 'S/.',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ECUADOR:
        return {
          Number: 34,
          Domain: 'sembo.com.ec',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-EC',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'USD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.URUGUAY:
        return {
          Number: 35,
          Domain: 'sembo.com.uy',
          IsSubdomain: false,
          Locale: 'es',
          FullLocale: 'es-UY',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'UYU',
            currencySymbol: '$U',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };

      case Domains.ISRAEL:
        return {
          Number: 36,
          Domain: 'sembo.co.il',
          IsSubdomain: false,
          Locale: 'he',
          FullLocale: 'he-IL',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'ILS',
            currencySymbol: '₪',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CROATIA:
        return {
          Number: 37,
          Domain: 'hr.sembo.com',
          IsSubdomain: true,
          Locale: 'hr',
          FullLocale: 'hr-HR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'HRK',
            currencySymbol: 'kn',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.INDONESIA:
        return {
          Number: 38,
          Domain: 'id.sembo.com',
          IsSubdomain: true,
          Locale: 'id',
          FullLocale: 'id-ID',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'IDR',
            currencySymbol: 'Rp',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SOUTHKOREA:
        return {
          Number: 39,
          Domain: 'kr.sembo.com',
          IsSubdomain: true,
          Locale: 'ko',
          FullLocale: 'ko-KR',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'KRW',
            currencySymbol: '₩',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.MALAYSIA:
        return {
          Number: 40,
          Domain: 'my.sembo.com',
          IsSubdomain: true,
          Locale: 'ms',
          FullLocale: 'ms-MY',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'MYR',
            currencySymbol: 'RM',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BELGIUM:
        return {
          Number: 41,
          Domain: 'sembo.be',
          IsSubdomain: false,
          Locale: 'nl',
          FullLocale: 'nl-BE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BRAZIL:
        return {
          Number: 42,
          Domain: 'sembo.com.br',
          IsSubdomain: false,
          Locale: 'pt',
          FullLocale: 'pt-BR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'BRL',
            currencySymbol: 'R$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ROMANIA:
        return {
          Number: 43,
          Domain: 'sembo.ro',
          IsSubdomain: false,
          Locale: 'ro',
          FullLocale: 'ro-RO',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'RON',
            currencySymbol: 'lei',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SERBIA:
        return {
          Number: 44,
          Domain: 'rs.sembo.com',
          IsSubdomain: true,
          Locale: 'sr',
          FullLocale: 'sr-RS',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'RSD',
            currencySymbol: 'Дин.',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.RUSSIA:
        return {
          Number: 45,
          Domain: 'sembo.ru',
          IsSubdomain: false,
          Locale: 'ru',
          FullLocale: 'ru-RU',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'RUB',
            currencySymbol: '₽',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SLOVAKIA:
        return {
          Number: 47,
          Domain: 'sk.sembo.com',
          IsSubdomain: true,
          Locale: 'sk',
          FullLocale: 'sk-SK',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.THAILAND:
        return {
          Number: 48,
          Domain: 'th.sembo.com',
          IsSubdomain: true,
          Locale: 'th',
          FullLocale: 'th-TH',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'THB',
            currencySymbol: '฿',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.VIETNAM:
        return {
          Number: 49,
          Domain: 'vn.sembo.com',
          IsSubdomain: true,
          Locale: 'vi',
          FullLocale: 'vi-VN',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'VND',
            currencySymbol: '₫',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.TAIWAN:
        return {
          Number: 50,
          Domain: 'tw.sembo.com',
          IsSubdomain: true,
          Locale: 'zh',
          FullLocale: 'zh-TW',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'TWD',
            currencySymbol: 'NT$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.HONGKONG:
        return {
          Number: 51,
          Domain: 'hk.sembo.com',
          IsSubdomain: true,
          Locale: 'zh',
          FullLocale: 'zh-HK',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'HKD',
            currencySymbol: '$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SWITZERLAND:
        return {
          Number: 52,
          Domain: 'ch.sembo.com',
          IsSubdomain: true,
          Locale: 'de',
          FullLocale: 'de-CH',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'CHF',
            currencySymbol: 'CHF',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/de/support/home',
        };
      case Domains.GREECE:
        return {
          Number: 53,
          Domain: 'sembo.gr',
          IsSubdomain: false,
          Locale: 'el',
          FullLocale: 'el-GR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BAHRAIN:
        return {
          Number: 54,
          Domain: 'bh.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-BH',
          MoneyFormat: {
            numberOfDecimals: 3,
            decimalSeparator: ',',
            currency: 'BHD',
            currencySymbol: '.د.ب',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BELARUS:
        return {
          Number: 55,
          Domain: 'by.sembo.com',
          IsSubdomain: true,
          Locale: 'be',
          FullLocale: 'be-BY',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'BYN',
            currencySymbol: 'Br',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.BOLIVIA:
        return {
          Number: 56,
          Domain: 'bo.sembo.com',
          IsSubdomain: true,
          Locale: 'es',
          FullLocale: 'es-BO',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'BOB',
            currencySymbol: '$b',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CHINA:
        return {
          Number: 57,
          Domain: 'cn.sembo.com',
          IsSubdomain: true,
          Locale: 'zh',
          FullLocale: 'zh-CN',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'CNY',
            currencySymbol: '¥',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CUBA:
        return {
          Number: 58,
          Domain: 'cu.sembo.com',
          IsSubdomain: true,
          Locale: 'es',
          FullLocale: 'es-CU',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'CUP',
            currencySymbol: '₱',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.CYPRUS:
        return {
          Number: 59,
          Domain: 'cy.sembo.com',
          IsSubdomain: true,
          Locale: 'el',
          FullLocale: 'el-CY',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.DOMINICANREPUBLIC:
        return {
          Number: 60,
          Domain: 'do.sembo.com',
          IsSubdomain: true,
          Locale: 'es',
          FullLocale: 'es-DO',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'DOP',
            currencySymbol: 'RD$',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.EGYPT:
        return {
          Number: 61,
          Domain: 'eg.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-EG',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EGP',
            currencySymbol: '£',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ESTONIA:
        return {
          Number: 62,
          Domain: 'sembo.ee',
          IsSubdomain: false,
          Locale: 'et',
          FullLocale: 'et-EE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.FRANCE:
        return {
          Number: 63,
          Domain: 'sembo.fr',
          IsSubdomain: false,
          Locale: 'fr',
          FullLocale: 'fr-FR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title:
            'Sembo: Construisez votre voyage - Hôtels, vols, ferries, activités, arrêts simples ou multiples!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ICELAND:
        return {
          Number: 64,
          Domain: 'sembo.is',
          IsSubdomain: false,
          Locale: 'is',
          FullLocale: 'is-IS',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'ISK',
            currencySymbol: 'kr',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.IRAN:
        return {
          Number: 65,
          Domain: 'ir.sembo.com',
          IsSubdomain: true,
          Locale: 'fa',
          FullLocale: 'fa-IR',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'IRR',
            currencySymbol: '﷼',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.ITALY:
        return {
          Number: 66,
          Domain: 'it.sembo.com',
          IsSubdomain: true,
          Locale: 'it',
          FullLocale: 'it-IT',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.JAPAN:
        return {
          Number: 67,
          Domain: 'jp.sembo.com',
          IsSubdomain: true,
          Locale: 'ja',
          FullLocale: 'ja-JP',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'JPY',
            currencySymbol: '¥',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.JORDAN:
        return {
          Number: 68,
          Domain: 'jo.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-JO',
          MoneyFormat: {
            numberOfDecimals: 3,
            decimalSeparator: '.',
            currency: 'JOD',
            currencySymbol: 'JD',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.KAZAKHSTAN:
        return {
          Number: 69,
          Domain: 'kz.sembo.com',
          IsSubdomain: true,
          Locale: 'kk',
          FullLocale: 'kk-KZ',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'KZT',
            currencySymbol: 'лв',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.KUWAIT:
        return {
          Number: 70,
          Domain: 'kw.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-KW',
          MoneyFormat: {
            numberOfDecimals: 3,
            decimalSeparator: ',',
            currency: 'KWD',
            currencySymbol: 'KD',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.LUXEMBOURG:
        return {
          Number: 71,
          Domain: 'lu.sembo.com',
          IsSubdomain: true,
          Locale: 'fr',
          FullLocale: 'fr-LU',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.MALTA:
        return {
          Number: 72,
          Domain: 'mt.sembo.com',
          IsSubdomain: true,
          Locale: 'mt',
          FullLocale: 'mt-MT',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.OMAN:
        return {
          Number: 73,
          Domain: 'om.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-OM',
          MoneyFormat: {
            numberOfDecimals: 3,
            decimalSeparator: ',',
            currency: 'OMR',
            currencySymbol: '﷼',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.PARAGUAY:
        return {
          Number: 74,
          Domain: 'py.sembo.com',
          IsSubdomain: true,
          Locale: 'es',
          FullLocale: 'es-PY',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'PYG',
            currencySymbol: 'Gs',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.QATAR:
        return {
          Number: 75,
          Domain: 'qa.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-QA',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'QAR',
            currencySymbol: '﷼',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.SAUDIARABIA:
        return {
          Number: 76,
          Domain: 'sa.sembo.com',
          IsSubdomain: true,
          Locale: 'ar',
          FullLocale: 'ar-SA',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'SAR',
            currencySymbol: '﷼',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.TURKEY:
        return {
          Number: 77,
          Domain: 'tr.sembo.com',
          IsSubdomain: true,
          Locale: 'tr',
          FullLocale: 'tr-TR',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'TRY',
            currencySymbol: '₺',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.UKRAINE:
        return {
          Number: 78,
          Domain: 'ua.sembo.com',
          IsSubdomain: true,
          Locale: 'uk',
          FullLocale: 'uk-UA',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'UAH',
            currencySymbol: '₴',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.VENEZUELA:
        return {
          Number: 79,
          Domain: 've.sembo.com',
          IsSubdomain: true,
          Locale: 'es',
          FullLocale: 'es-VE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'VES',
            currencySymbol: 'Bs.',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: defaultSupportPage,
        };
      case Domains.DENMARK:
        return {
          Number: 5,
          Domain: 'sembo.dk',
          IsSubdomain: false,
          Locale: 'dk',
          FullLocale: 'da-DK',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'DKK',
            currencySymbol: 'kr',
          },
          Logo: 'dtf_logo_v2.svg',
          Title:
            'Sembo: Skab din rejse - Hoteller, flyrejser, færger, aktiviteter, enkelt eller flere stop!',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/da/support/home',
        };
      case Domains.DTFDENMARK:
        return {
          Number: 405,
          Domain: 'dtf-travel.dk',
          IsSubdomain: false,
          Locale: 'dk',
          FullLocale: 'da-DK',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: ',',
            currency: 'DKK',
            currencySymbol: 'kr',
          },
          Logo: 'dtf_logo_v2.svg',
          Title: 'DTF Travel',
          GeneralTermsRelativePath: '/pages/terms-dtf.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/da/support/home',
        };
      case Domains.STENASWEDEN:
        return {
          Number: 101,
          Domain: 'sembo.stenaline.se',
          IsSubdomain: true,
          Locale: 'sv',
          FullLocale: 'sv-SE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'SEK',
            currencySymbol: 'kr',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENANORWAY:
        return {
          Number: 104,
          Domain: 'sembo.stenaline.no',
          IsSubdomain: true,
          Locale: 'no',
          FullLocale: 'nb-NO',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'NOK',
            currencySymbol: 'kr',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENADENMARK:
        return {
          Number: 105,
          Domain: 'sembo.stenaline.dk',
          IsSubdomain: true,
          Locale: 'dk',
          FullLocale: 'da-DK',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: ',',
            currency: 'DKK',
            currencySymbol: 'kr',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENAFINLAND:
        return {
          Number: 106,
          Domain: 'sembo.stenaline.fi',
          IsSubdomain: true,
          Locale: 'fi',
          FullLocale: 'fi-FI',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENAGERMANY:
        return {
          Number: 107,
          Domain: 'sembo.stenaline.de',
          IsSubdomain: true,
          Locale: 'de',
          FullLocale: 'de-DE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENAPOLAND:
        return {
          Number: 108,
          Domain: 'sembo.stenaline.pl',
          IsSubdomain: true,
          Locale: 'pl',
          FullLocale: 'pl-PL',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'PLN',
            currencySymbol: 'zł',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENANETHERLAND:
        return {
          Number: 109,
          Domain: 'sembo.stenaline.nl',
          IsSubdomain: true,
          Locale: 'nl',
          FullLocale: 'nl-NL',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENAUNITEDKINGDOM:
        return {
          Number: 110,
          Domain: 'sembo.stenaline.co.uk',
          IsSubdomain: true,
          Locale: 'en',
          FullLocale: 'en-GB',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'GBP',
            currencySymbol: '£',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      case Domains.STENAIRELAND:
        return {
          Number: 111,
          Domain: 'sembo.stenaline.ie',
          IsSubdomain: true,
          Locale: 'en',
          FullLocale: 'en-IE',
          MoneyFormat: {
            numberOfDecimals: 2,
            decimalSeparator: '.',
            currency: 'EUR',
            currencySymbol: '€',
          },
          Logo: '',
          Title: 'StenaLine',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: true,
          SupportPage: defaultSupportPage,
        };
      default:
        return {
          Number: 1,
          Domain: 'sembo.se',
          IsSubdomain: false,
          Locale: 'sv',
          FullLocale: 'sv-SE',
          MoneyFormat: {
            numberOfDecimals: 0,
            decimalSeparator: '.',
            currency: 'SEK',
            currencySymbol: 'kr',
          },
          Logo: 'sembo_logo_blank.svg',
          Title: 'Sembo',
          GeneralTermsRelativePath: '/pages/terms.aspx',
          IsStenaCompany: false,
          SupportPage: 'https://sembo.freshdesk.com/sv-SE/support/home',
        };
    }
  }

  public get isWhiteLabel(): boolean {
    return this.hostContains('whitelabel');
  }

  public get isStenaLine(): boolean {
    return this.hostContains('stenaline');
  }

  public get isLocalhost(): boolean {
    return this.hostContains('localhost') || this.hostContains('127.0.0.1');
  }

  public get isStaging(): boolean {
    return this.hostContains('sembo-stage');
  }

  public get isProduction(): boolean {
    return !this.isLocalhost && !this.isStaging;
  }

  public get isDanishMarket(): boolean {
    return getDomain.getCurrentDomainSpec().Locale === 'dk';
  }

  public get isGermanMarket(): boolean {
    return getDomain.getCurrentDomainSpec().Locale === 'de';
  }

  public getCurrentDomainSpec(): Market {
    if (isDefined(this.currentDomainSpec)) {
      return this.currentDomainSpec;
    }

    const currentHost: string = window.location.host
      .replace('www.', '')
      .replace('whitelabel.', '');

    let currentDomain: Market = this.getDomainSpec(Domains.SWEDEN);

    if (this.isProduction) {
      Object.keys(Domains).forEach((key: string) => {
        const domain: Market = this.getDomainSpec(
          Domains[key as keyof typeof Domains]
        );
        if (currentHost === domain.Domain) {
          currentDomain = domain;
          return;
        }
      });
    } else {
      const defaultLocale = 'en';
      const locale =
        this.parseQueryString(window.location.href).locale ?? defaultLocale;

      Object.keys(Domains).forEach((key: string) => {
        const domain: Market = this.getDomainSpec(
          Domains[key as keyof typeof Domains]
        );
        if (locale.includes(domain.Locale) && !domain.IsStenaCompany) {
          currentDomain = domain;
        }
      });
    }

    this.currentDomainSpec = currentDomain;

    return currentDomain;
  }

  public getLanguageLocale(): string {
    if (this.isProduction) {
      return this.getCurrentDomainSpec().Locale;
    }

    const defaultUserLocale = 'en';
    const queryStringLocale = this.parseQueryString(
      window.location.href
    ).locale;

    if (Array.isArray(queryStringLocale)) {
      assertValue(queryStringLocale[0], '');
      return queryStringLocale[0];
    }
    return queryStringLocale ?? defaultUserLocale;
  }

  private parseQueryString(
    query: string
  ): Record<string, string | string[] | undefined> {
    const queryString: Record<string, string | string[]> = {};
    const queryParams = query.split('?')[1];

    if (!queryParams) {
      return queryString;
    }

    queryParams.split('&').forEach((param) => {
      const [key, value] = param.split('=').map(decodeURIComponent);
      assertValue(key, 'domainMapping.ts > parseQueryString > key');
      assertValue(value, 'domainMapping.ts > parseQueryString > value');

      if (!queryString[key]) {
        queryString[key] = value;
      } else if (typeof queryString[key] === 'string') {
        queryString[key] = [queryString[key] as string, value];
      } else {
        (queryString[key] as string[]).push(value);
      }
    });

    return queryString;
  }

  private hostContains(value: string): boolean {
    return window.location.host.includes(value);
  }
}

export enum Domains {
  ARGENTINA,
  AUSTRALIA,
  AUSTRIA,
  BAHRAIN,
  BELARUS,
  BELGIUM,
  BOLIVIA,
  BRAZIL,
  BULGARIA,
  CANADA,
  CHILE,
  CHINA,
  COLOMBIA,
  CROATIA,
  CUBA,
  CYPRUS,
  CZECHREPUBLIC,
  DENMARK,
  DOMINICANREPUBLIC,
  DTFDENMARK,
  ECUADOR,
  EGYPT,
  ESTONIA,
  FINLAND,
  FRANCE,
  GERMANY,
  GREECE,
  HONGKONG,
  HUNGARY,
  ICELAND,
  INDIA,
  INDONESIA,
  IRAN,
  IRELAND,
  ISRAEL,
  ITALY,
  JAPAN,
  JORDAN,
  KAZAKHSTAN,
  KUWAIT,
  LUXEMBOURG,
  MALAYSIA,
  MALTA,
  MEXICO,
  NETHERLAND,
  NEWZEELAND,
  NIGERIA,
  NORWAY,
  OMAN,
  PARAGUAY,
  PERU,
  PHILIPPINES,
  POLAND,
  PORTUGAL,
  QATAR,
  ROMANIA,
  RUSSIA,
  SAUDIARABIA,
  SERBIA,
  SINGAPORE,
  SLOVAKIA,
  SOUTHAFRICA,
  SOUTHKOREA,
  SPAIN,
  SWEDEN,
  STENASWEDEN,
  STENANORWAY,
  STENADENMARK,
  STENAFINLAND,
  STENAGERMANY,
  STENAPOLAND,
  STENANETHERLAND,
  STENAUNITEDKINGDOM,
  STENAIRELAND,
  SWITZERLAND,
  TAIWAN,
  THAILAND,
  TURKEY,
  UKRAINE,
  UNITEDARABEMIRATES,
  UNITEDKINGDOM,
  URUGUAY,
  USA,
  VENEZUELA,
  VIETNAM,
}

export interface Market {
  Number: number;
  Domain: string;
  IsSubdomain: boolean;
  Locale: string;
  FullLocale: string;
  MoneyFormat: CompanyMoneyFormatWithSymbol;
  Logo: string;
  Title: string;
  GeneralTermsRelativePath: string;
  IsStenaCompany: boolean;
  SupportPage: string;
}

export const getDomain = new DomainMapping();
